import { ThemeColorArg, getThemeColor } from '@rbilabs/components-library';
import styled, { css } from 'styled-components';

import ActionButton from 'components/action-button';

import theme from './theme';
import { BottomServiceVariants } from './types';

//TODO handle colors when background switches
export const mapVariantToColor = (
  variant: BottomServiceVariants,
  isButtonTextColor = false
): ThemeColorArg => {
  switch (variant) {
    case BottomServiceVariants.IN_PROGRESS:
      return 'bsm-in-progress-background-color';
    case BottomServiceVariants.IN_RESTAURANT_LOYALTY:
      return 'background-contrast-loyalty';
    case BottomServiceVariants.DEFAULT:
    default:
      return isButtonTextColor ? 'text-link-default' : 'bsm-default-background-color';
  }
};

export const mapTextToColor = (variant: BottomServiceVariants) => {
  switch (variant) {
    case BottomServiceVariants.IN_RESTAURANT_LOYALTY:
    case BottomServiceVariants.IN_PROGRESS:
      return 'text-reversed';
    case BottomServiceVariants.DEFAULT:
    default:
      return 'text-link-default';
  }
};

export const mapVariantToIconStyles = (variant: BottomServiceVariants) => {
  switch (variant) {
    case BottomServiceVariants.IN_RESTAURANT_LOYALTY:
      return css`
        align-self: flex-start;
        justify-self: center;
        svg {
          height: 1.5rem;
        }
      `;
    default:
      return css`
        align-self: center;
        justify-self: baseline;
      `;
  }
};

export const ButtonContainer = styled.div<{
  $badgeContent: string | number | undefined;
}>`
  grid-area: button;
  align-self: center;
  justify-self: center;
  display: flex;
  position: relative;

  & > :not(:last-child) {
    margin-right: 0.625rem;
  }

  ${({ $badgeContent }) =>
    $badgeContent &&
    css`
      ::after {
        background: ${Styles.color.red};
        color: ${Styles.color.white};
        border-radius: ${Styles.borderRadius};
        content: '${$badgeContent}';
        font-family:${Styles.fontFamily.base};
        font-size: 0.725rem;
        height: 1.125rem;
        position: absolute;
        right: -0.125rem;
        text-align: center;
        top: -0.5rem;
        width: 1.125rem;
      }
    `}
`;

export const IconContainer = styled.div`
  grid-area: icon;
`;

export const NextIconContainer = styled.div`
  grid-area: next-icon;
`;

export const Container = styled.div<{ variant: BottomServiceVariants }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.475rem;
  height: 4.125rem;
  background: ${props => getThemeColor(props.theme, mapVariantToColor(props.variant))};
  cursor: pointer;
  box-shadow: ${p => p.theme.token('bsm-drop-shadow')};

  ${IconContainer} {
    ${props => mapVariantToIconStyles(props.variant)}
  }
`;

export const LocationOverlayButton = styled.a`
  grid-area: 1 / 1 / span 3 / span 2;
  width: auto;
  min-height: 100%;
  opacity: 0;
  z-index: ${Styles.zIndex.top};
  margin-left: -20%;
  font-family: ${Styles.fontFamily.base};
  ${Styles.desktop`
    margin-left: -5%;
  `}
  cursor: pointer;
  text-decoration: none;
`;

export const BottomServiceWrapper = styled.div`
  max-width: ${Styles.layout.sectionMaxWidth};
  color: ${p => p.theme.token(theme.bottomServiceWrapper.color)};
  display: grid;
  grid-template-areas:
    'icon heading button'
    'icon details button';
  grid-template-columns: 2.2rem auto max-content;
  width: 100%;
`;

export const Heading = styled.div<{ variant: BottomServiceVariants }>`
  grid-area: heading;
  font-family: ${Styles.fontFamily.base};
  font-size: 1rem;
  color: ${p => p.theme.token(mapTextToColor(p.variant))};
  padding: 0 0.625rem;
  text-transform: ${theme.textTransform};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding-right: 0.75rem;
`;

export const Details = styled.div<{ variant: BottomServiceVariants }>`
  grid-area: details;
  color: ${p => p.theme.token(mapTextToColor(p.variant))};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0 0.625rem;
`;

export const AddressStyle = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding-right: 0.75rem;
`;

export const Underline = styled.div`
  background-color: ${p => p.theme.token(theme.underline.color)};
  width: 100%;
  height: 0.125rem;
`;

export const LocationStyle = styled.div`
  display: flex;
  justify-content: space-between;

  ${Styles.desktop`
    height: 1.5rem;
  `}
`;

export const StyledBottomServiceContainer = styled.div<{ variant: BottomServiceVariants }>`
  height: 8.875rem;
  position: relative;
  cursor: pointer;
  box-shadow: ${p => p.theme.token('bsm-drop-shadow')};
  background-color: ${Styles.color.white};
  display: flex;
  align-items: center;
  justify-content: center;

  ${IconContainer} {
    ${props => mapVariantToIconStyles(props.variant)}
  }

  ${NextIconContainer} {
    ${props => mapVariantToIconStyles(props.variant)}
  }

  ${Styles.desktop`
    height: 5.425rem;
    background-color: ${(props: any) =>
      getThemeColor(props.theme, mapVariantToColor(props.variant))};
  `}
`;

export const StyledInnerWrapper = styled.div`
  max-width: ${Styles.layout.sectionMaxWidth};
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.token(theme.bottomServiceWrapper.color)};

  ${Styles.desktop`
    flex-direction: row;
  `}
`;

export const StyledDetailsButton = styled.button<{ variant: BottomServiceVariants }>`
  width: 100%;
  height: 72px;
  padding: 0.475rem;
  display: grid;
  grid-template-areas:
    'icon heading next-icon'
    'icon details next-icon';
  grid-template-columns: 2.2rem auto 2.2rem;
  font-family: ${Styles.fontFamily.base};
  background-color: ${props => getThemeColor(props.theme, mapVariantToColor(props.variant))};
  cursor: pointer;
  border: none;

  ${Styles.desktop`
    height: 86px;
  `}
`;

export const StyledButtonsContainer = styled(ButtonContainer)<{ variant: BottomServiceVariants }>`
  padding: 0.475rem;
  background-color: ${Styles.color.white};
  height: 70px;

  ${Styles.desktop`
    height: 100%;
    background-color: ${(props: any) =>
      getThemeColor(props.theme, mapVariantToColor(props.variant))};
  `}
`;

export const StyledHeading = styled(Heading)`
  text-align: start;
  margin-top: auto;
  font-size: 0.875rem;
  font-weight: ${Styles.fontWeight.heavy};
  line-height: 1.25rem;
`;

export const StyledDetailsText = styled(Details)`
  font-size: 0.875rem;
  line-height: 1.25rem;
  text-align: start;
`;

export const StyledActionButton = styled(ActionButton)`
  padding: 0.75rem 0.88rem;
  font-size: 1rem;
  line-height: 1rem;

  ${Styles.desktop`
   padding: 0.75rem 2.5rem;
  `}
`;

export const StyledDateTimeButtonText = styled.span`
  font-size: 0.75rem;
  text-align: start;
  display: block;
`;
